import styled, { css, keyframes } from 'styled-components';
import { PRINT_PAGE_MM } from './programModel';

// The public program in the look of the HEARTPORT crew page (public.heartport.org/crew): a light
// sky-blue band with the lung-flower illustration, white rounded cards on a vertical timeline, round
// portraits wherever a person is named. Roboto, like the rest of the event page.
//
// Contrast (WCAG AA, on white cards and on the light band): body text INK >= 13:1, secondary text
// MUTED >= 5.6:1, small labels in ACCENT_TEXT >= 5.3:1. ACCENT (#47ABDB) is only 2.6:1 on white, so it
// is used for lines, dots, borders and fills only; the large section title uses TITLE (>= 3.4:1,
// large text). The PDF button takes the REGISTRIEREN colours of the event page (white on ACCENT, 2.6:1,
// ACCENT_DARK on hover); the other buttons are white on ACCENT_DARK (7:1).
//
// Print (the admin's PDF export, ProgramPrint.js): every component also reads `$print`-free CSS under
// `@media print`, so the same markup prints on white A4 without shadows, animations or decoration.

export const ACCENT = '#47ABDB';
export const ACCENT_DARK = '#0056b3';
export const ACCENT_TEXT = '#17689a';
export const HIGHLIGHT = '#14cbde';
export const INK = '#0b1f3a';
const TITLE = '#2a8bc0';
const MUTED = '#4f6078';
const LINE = '#e1eaf2';
const CHIP = '#eef5fb';
const CHIP_HOVER = '#dcecf9';
export const BAND = '#d6e8f4';

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

export const Section = styled.section`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #cfe3f1 0%, #e4eff8 55%, #d3e6f3 100%);
  color: ${INK};
  overflow-wrap: anywhere;

  @media print {
    background: transparent;
    overflow: visible;
  }
`;

export const SectionInner = styled.div`
  position: relative;
  /* above the flowers (0) and the butterflies (1, ProgramButterflies) */
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 40px 64px;

  @media screen and (max-width: 768px) {
    padding: 48px 16px 36px;
  }

  @media print {
    max-width: none;
    padding: 0;
  }
`;

// The illustrations of the crew page (src/assets/images): decoration only, behind the content.
export const Deco = styled.img`
  position: absolute;
  z-index: 0;
  pointer-events: none;
  user-select: none;

  ${(p) => p.$variant === 'lung' && css`
    right: -70px;
    top: 30px;
    width: min(360px, 30vw);
    opacity: 0.95;

    @media print {
      display: block !important;
      right: 0;
      top: 0;
      width: 24mm;
      opacity: 0.9;
    }

    @media screen and (max-width: 768px) {
      right: -60px;
      top: 0;
      width: 170px;
      opacity: 0.45;
    }
  `}

  @media print {
    display: none;
  }
`;

// The flowers, hearts and lungs of the PDF (ProgramSectionView's printScatter), in a layer as large as
// the section that cuts off what lies past it, so the decoration of pages the program does not reach
// makes no empty pages. Positions are in mm of the printed flow, page by page. Print only.
export const PrintDecoLayer = styled.div`
  display: none;

  @media print {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
  }
`;

export const PrintScatter = styled.img`
  position: absolute;
  top: ${(p) => p.$top}mm;
  ${(p) => p.$side}: ${(p) => p.$offset}mm;
  width: ${(p) => p.$width}mm;
  opacity: ${(p) => p.$opacity};
  transform: ${(p) => `${p.$mirror ? 'scaleX(-1) ' : ''}rotate(${p.$rotate || 0}deg)`};
`;

// The flowers scattered down the band (ProgramSectionView's SCATTER): in the side gutters, partly
// behind the cards, alternating left and right. Wide screens only, except the ones marked for phones,
// which shrink into the corner of the gutter. Never printed.
export const Scatter = styled.img`
  position: absolute;
  z-index: 0;
  pointer-events: none;
  user-select: none;
  top: ${(p) => p.$top};
  ${(p) => p.$side}: ${(p) => p.$offset}px;
  width: ${(p) => p.$width}px;
  opacity: ${(p) => p.$opacity};
  transform: ${(p) => `${p.$mirror ? 'scaleX(-1) ' : ''}rotate(${p.$rotate || 0}deg)`};

  @media screen and (max-width: 1024px) {
    ${(p) => (p.$phone ? css`
      width: ${Math.round(p.$width * 0.45)}px;
      ${p.$side}: -40px;
      opacity: ${Math.min(p.$opacity, 0.35)};
    ` : css`display: none;`)}
  }

  @media print {
    display: none;
  }
`;

export const SectionTitle = styled.h2`
  color: ${TITLE};
  text-align: center;
  font-size: 3rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin: 0 0 14px;

  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }

  @media print {
    font-size: 26pt;
    color: ${INK};
  }
`;

export const AccentBar = styled.div`
  width: 70px;
  height: 4px;
  margin: 0 auto 24px;
  border-radius: 2px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);

  @media print {
    margin-bottom: 12px;
  }
`;

export const Notes = styled.p`
  text-align: center;
  color: ${MUTED};
  font-size: 1.02rem;
  margin: 0 auto 28px;
  max-width: 680px;
  white-space: pre-line;

  @media print {
    font-size: 12pt;
    max-width: 120mm;
    margin-bottom: 14px;
  }
`;

export const DayTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 28px;

  @media print {
    display: none;
  }
`;

export const DayTab = styled.button`
  background: ${(p) => (p.$active ? INK : 'white')};
  color: ${(p) => (p.$active ? 'white' : INK)};
  border: 1px solid ${(p) => (p.$active ? INK : '#c4d6e6')};
  max-width: 100%;
  font-weight: bold;
  font-size: 1rem;
  padding: 0.5rem 1.15rem;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;

  &:hover, &:focus-visible {
    box-shadow: 0 0 0 2px ${ACCENT};
    outline: none;
  }
`;

export const DayHeading = styled.h3`
  text-align: center;
  font-size: 1.15rem;
  font-weight: 500;
  color: ${INK};
  margin: 0 0 32px;

  @media screen and (max-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 22px;
  }

  @media print {
    font-size: 13pt;
    margin-bottom: 14px;
  }
`;

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: white;
  border: 1px solid ${HIGHLIGHT};
  border-radius: 14px;
  padding: 0.7rem 1rem;
  margin: 0 auto 24px;
  max-width: 920px;
  font-size: 1rem;
  box-shadow: 0 6px 18px rgba(11, 31, 58, 0.06);
`;

export const TextButton = styled.button`
  background: none;
  border: none;
  color: ${ACCENT_DARK};
  font-weight: bold;
  text-decoration: underline;
  font-size: inherit;
  cursor: pointer;
  padding: 0.2rem;

  &:hover, &:focus-visible {
    color: ${INK};
  }
`;

// ------------------------------------------------------------------ the timeline

const TIME_COL = 96;
const TIME_GAP = 36;

const rise = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
`;

// One time slot: the time on the left, a dot on the timeline, the (parallel) blocks on the right;
// stacked on phones, where the line and dots go away and the time sits above its card.
export const Slot = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${TIME_COL}px 1fr;
  gap: 0 ${TIME_GAP}px;
  max-width: 920px;
  margin: 0 auto 22px;
  animation: ${rise} 0.6s ease both;
  ${reducedMotion}

  /* the fade-in makes every slot its own stacking context: the slot with an open person card goes
     on top, so the card is not covered by the next slot */
  &:has([role="dialog"]) {
    z-index: 20;
  }

  /* the timeline, drawn per slot so that it runs through the gaps between the slots too */
  &::before {
    content: "";
    position: absolute;
    left: ${TIME_COL + TIME_GAP / 2 - 1}px;
    top: 0;
    bottom: -22px;
    width: 2px;
    background: ${(p) => (p.$passed ? ACCENT_DARK : 'rgba(71, 171, 219, 0.45)')};
  }

  &:last-of-type::before {
    bottom: 0;
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-bottom: 16px;

    &::before {
      display: none;
    }
  }

  @media print {
    animation: none;
    grid-template-columns: 62pt 1fr;
    gap: 0 12pt;
    margin-bottom: 8pt;
    max-width: none;

    &::before {
      display: none;
    }
  }
`;

export const SlotTime = styled.div`
  position: relative;
  text-align: right;
  font-size: 1.2rem;
  font-weight: bold;
  color: ${INK};
  padding-top: 20px;
  font-variant-numeric: tabular-nums;

  /* the dot on the timeline */
  &::after {
    content: "";
    position: absolute;
    top: 25px;
    right: ${-(TIME_GAP / 2) - 8}px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => (p.$reached ? ACCENT_DARK : 'white')};
    border: 3px solid ${(p) => (p.$reached ? ACCENT_DARK : ACCENT)};
  }

  @media screen and (max-width: 768px) {
    text-align: left;
    color: ${ACCENT_TEXT};
    font-size: 1.05rem;
    padding: 0 0 6px 4px;

    &::after {
      display: none;
    }

    /* every card of the slot prints its own start and end: no need to repeat the start above it */
    ${(p) => p.$covered && css`display: none;`}
  }

  @media print {
    font-size: 13pt;
    padding-top: 6pt;
    text-align: left;

    &::after {
      display: none;
    }
  }
`;

// ------------------------------------------------------------------ "now" on the day of the event

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.8); }
  70% { box-shadow: 0 0 0 6px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

// "Happening now" on a running entry: white on ACCENT_DARK (7:1), with a pulsing dot; square corners
// like the buttons of the event page (REGISTRIEREN, the day tabs).
export const NowBadge = styled.strong`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  background: ${ACCENT_DARK};
  color: white;
  font-size: 0.72rem;
  font-weight: bold;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.15rem 0.6rem;
  margin-bottom: 0.3rem;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: white;
    animation: ${pulse} 2s ease-out infinite;
    ${reducedMotion}
  }

  @media print {
    display: none;
  }
`;

// The part of the timeline in the slot of "now" that has passed (its `height` set inline to the line's
// `top`): dark like the label, as the timeline of the slots before it, so scrolling back shows how far
// the congress has got; the rest stays light. Not on phones (no timeline) or in print.
export const NowPassed = styled.div`
  position: absolute;
  top: 0;
  left: ${TIME_COL + TIME_GAP / 2 - 1}px;
  width: 2px;
  background: ${ACCENT_DARK};
  pointer-events: none;
  transition: height 0.6s ease;
  ${reducedMotion}

  @media screen and (max-width: 768px) {
    display: none;
  }

  @media print {
    display: none;
  }
`;

// The phones' button to what runs now (ProgramSectionView; as a link to the program on the other pages,
// LiveProgramButton): small, at the bottom right above the
// browser's bar, square like the site's buttons; under the person card and the install offer.
export const JumpButton = styled.button`
  display: none;

  @media screen and (max-width: 768px) {
    position: fixed;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    z-index: 25;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    border: none;
    background: ${ACCENT_DARK};
    color: white;
    text-decoration: none;
    font-size: 0.8rem;
    font-weight: bold;
    padding: 0.5rem 0.8rem;
    box-shadow: 0 6px 18px rgba(11, 31, 58, 0.25);
    cursor: pointer;
    animation: ${rise} 0.3s ease both;
    ${reducedMotion}

    &:focus-visible {
      outline: 2px solid ${ACCENT};
      outline-offset: 2px;
    }
  }

  @media print {
    display: none;
  }
`;

// "Now" in the slot that holds it (its `top` set inline): a short line from the time column to a dot on
// the timeline, never across the cards; the label with the time stands above it in the time column. On
// phones, where there is no timeline, only the label shows, in the gap above the running entry.
export const NowLine = styled.div`
  position: absolute;
  left: 0;
  width: ${TIME_COL + TIME_GAP / 2}px;
  height: 0;
  border-top: 2px solid ${ACCENT_DARK};
  pointer-events: none;
  transition: top 0.6s ease;
  ${reducedMotion}

  &::after {
    content: "";
    position: absolute;
    top: -8px;
    left: ${TIME_COL + TIME_GAP / 2 - 7}px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${ACCENT_DARK};
    border: 2px solid white;
  }

  @media screen and (max-width: 768px) {
    z-index: 3;
    border-top: none;

    &::after {
      display: none;
    }
  }

  @media print {
    display: none;
  }
`;

export const NowPill = styled.span`
  position: absolute;
  z-index: 2;
  left: 0;
  top: 0;
  transform: translateY(-50%);
  background: ${ACCENT_DARK};
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  padding: 0.1rem 0.55rem;
`;

export const SlotEntries = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => Math.min(p.$columns || 1, 3)}, minmax(0, 1fr));
  gap: 14px;

  @media screen and (max-width: 1024px) {
    grid-template-columns: repeat(${(p) => Math.min(p.$columns || 1, 2)}, minmax(0, 1fr));
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const highlighted = css`
  box-shadow: 0 0 0 3px ${HIGHLIGHT}, 0 10px 30px rgba(20, 203, 222, 0.25);
`;

// A block that runs now (the day of the event, liveProgram.js); a person's highlight wins over it.
const runningNow = css`
  box-shadow: 0 0 0 2px ${ACCENT}, 0 10px 30px rgba(71, 171, 219, 0.3);
`;

// An entry that is over (the day of the event): grey instead of blue, portraits paled, a block's card
// translucent and flat, so the program reads as done up to now. Text keeps its contrast (grayscale keeps
// the lightness); hovering, focusing or an open person card shows it as usual again.
const pastEntry = css`
  filter: grayscale(1);
  transition: filter 0.25s, opacity 0.25s, background-color 0.25s;

  img {
    opacity: 0.6;
  }

  &:hover,
  &:focus-within,
  &:has([role="dialog"]) {
    filter: none;

    img {
      opacity: 1;
    }
  }

  @media print {
    filter: none;
  }
`;

// A top-level block: a white card. Breaks, arrival and social items are a quiet row instead.
export const Block = styled.article`
  position: relative;
  background: ${(p) => (p.$quiet ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.94)')};
  border-radius: 20px;
  padding: ${(p) => (p.$quiet ? '0.9rem 1.5rem' : '1.4rem 1.6rem')};
  box-shadow: ${(p) => (p.$quiet ? 'none' : '0 6px 24px rgba(11, 31, 58, 0.07)')};
  scroll-margin-top: 110px;
  transition: box-shadow 0.25s, transform 0.25s;
  ${(p) => !p.$quiet && css`
    &:hover {
      box-shadow: 0 12px 34px rgba(11, 31, 58, 0.12);
    }
  `}
  ${(p) => p.$past && css`
    ${pastEntry}
    background: rgba(255, 255, 255, 0.5);
    box-shadow: none;
  `}
  ${(p) => p.$now && runningNow}
  ${(p) => p.$highlight && highlighted}
  ${reducedMotion}

  @media screen and (max-width: 768px) {
    border-radius: 16px;
    padding: ${(p) => (p.$quiet ? '0.75rem 1rem' : '1rem')};
  }

  @media print {
    /* a little translucent, so the flowers behind (printScatter) show softly through */
    background: ${(p) => (p.$quiet ? 'rgba(255, 255, 255, 0.55)' : 'rgba(255, 255, 255, 0.76)')};
    box-shadow: none;
    border: 0.75pt solid ${(p) => (p.$quiet ? '#bcd3e4' : '#d5e3ee')};
    border-radius: 8pt;
    padding: ${(p) => (p.$quiet ? '4pt 10pt' : '8pt 10pt')};
    break-inside: auto;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    ${(p) => p.$quiet && css`border-style: dashed;`}
  }
`;

// The head of a block: its text on the left, the faces of everyone in it on the right.
export const BlockHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  @media screen and (max-width: 768px) {
    display: block;
  }
`;

export const BlockHeadText = styled.div`
  min-width: 0;
`;

// Overlapping round portraits of the people in a block (decoration: the names follow in the text).
export const AvatarStack = styled.div`
  display: flex;
  flex: none;
  padding-left: 12px;

  @media screen and (max-width: 768px) {
    display: none;
  }

  & > * {
    margin-left: -12px;
    border: 3px solid white;
    box-sizing: content-box;
  }

  @media print {
    & > * {
      border-width: 1.5pt;
    }
  }
`;

export const StackMore = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${CHIP};
  color: ${INK};
  font-size: 0.8rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CHILD_INDENT = '7rem';

// A child (depth 1) or grandchild (depth 2), separated by a hairline under its parent.
export const Child = styled.div`
  position: relative;
  margin-top: ${(p) => (p.$depth > 1 ? '0.6rem' : '0.9rem')};
  margin-left: ${(p) => (p.$depth > 1 ? '0.2rem' : '0')};
  padding: ${(p) => (p.$depth > 1 ? '0.15rem 0 0.15rem 0.9rem' : `0.9rem 0 0.1rem ${CHILD_INDENT}`)};
  border-top: ${(p) => (p.$depth > 1 ? 'none' : `1px solid ${LINE}`)};
  border-left: ${(p) => (p.$depth > 1 ? `2px solid ${CHIP_HOVER}` : 'none')};
  border-radius: ${(p) => (p.$depth > 1 ? '0' : '0')};
  scroll-margin-top: 110px;
  transition: background-color 0.3s, box-shadow 0.3s;
  ${(p) => p.$past && pastEntry}
  ${(p) => p.$now && css`
    background: rgba(71, 171, 219, 0.12);
    box-shadow: -8px 0 0 rgba(71, 171, 219, 0.12), 8px 0 0 rgba(71, 171, 219, 0.12);
    border-radius: 8px;
  `}
  ${(p) => p.$highlight && css`
    background: rgba(20, 203, 222, 0.1);
    box-shadow: -8px 0 0 rgba(20, 203, 222, 0.1), 8px 0 0 rgba(20, 203, 222, 0.1);
    border-radius: 8px;
  `}

  @media screen and (max-width: 768px) {
    padding-left: ${(p) => (p.$depth > 1 ? '0.9rem' : '0')};
  }

  @media print {
    margin-top: ${(p) => (p.$depth > 1 ? '4pt' : '7pt')};
    padding-top: ${(p) => (p.$depth > 1 ? '0' : '7pt')};
    padding-left: ${(p) => (p.$depth > 1 ? '10pt' : '78pt')};
    break-inside: avoid;
  }
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.6rem;
  margin-bottom: 0.3rem;
  font-size: 0.88rem;
  color: ${MUTED};

  @media print {
    font-size: 10.5pt;
  }

  /* the block's time range: on phones the only time of the block, so it reads as one */
  & > span:first-child {
    font-variant-numeric: tabular-nums;
  }

  @media screen and (max-width: 768px) {
    & > span:first-child {
      color: ${ACCENT_TEXT};
      font-weight: bold;
      font-size: 0.95rem;
    }
  }
`;

export const Tag = styled.span`
  display: inline-block;
  background: ${CHIP};
  color: ${ACCENT_TEXT};
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: bold;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.6rem;
  text-transform: uppercase;
`;

export const VenueNote = styled.span`
  font-style: italic;
`;

export const Eyebrow = styled.div`
  color: ${ACCENT_TEXT};
  font-weight: bold;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

export const EntryTitle = styled.h4`
  font-size: ${(p) => (p.$depth === 0 ? '1.3rem' : p.$depth === 1 ? '1.02rem' : '0.98rem')};
  font-weight: ${(p) => (p.$depth === 0 ? 800 : p.$depth === 1 ? 600 : 500)};
  text-transform: ${(p) => (p.$depth === 0 ? 'uppercase' : 'none')};
  letter-spacing: ${(p) => (p.$depth === 0 ? '0.01em' : '0')};
  color: ${INK};
  line-height: 1.3;
  margin: 0;
  @media screen and (max-width: 768px) {
    font-size: ${(p) => (p.$depth === 0 ? '1.12rem' : '0.98rem')};
  }

  /* print: level 1 (block) 15pt black caps, level 2 (talk) 12.5pt semibold */
  @media print {
    font-size: ${(p) => (p.$depth === 0 ? '15pt' : '12.5pt')};
    line-height: 1.25;
  }
`;

// The time of a talk: in the indent left of the talk on wide screens, above its title on phones.
export const ChildTime = styled.span`
  display: inline-block;
  ${(p) => p.$depth === 1 && css`
    position: absolute;
    left: 0;
    top: calc(0.9rem + 2px);
  `}
  min-width: 6.2rem;
  color: ${ACCENT_TEXT};
  font-weight: bold;
  font-size: 0.85rem;
  margin-right: 0.6rem;
  font-variant-numeric: tabular-nums;

  @media screen and (max-width: 768px) {
    position: static;
    display: block;
    margin-bottom: 0.1rem;
  }

  @media print {
    min-width: 0;
    font-size: 11pt;
    top: 8pt;
  }
`;

export const Subtitle = styled.div`
  color: ${MUTED};
  font-size: 0.98rem;
  margin-top: 0.25rem;

  @media print {
    font-size: 11pt;
    font-style: italic;
    margin-top: 1pt;
  }
`;

export const Focus = styled.div`
  color: ${MUTED};
  font-style: italic;
  font-size: 0.94rem;
  margin-top: 0.3rem;
`;

export const Discussion = styled.div`
  color: ${MUTED};
  font-size: 0.85rem;
  margin-top: 0.35rem;
`;

export const PeopleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.65rem;
  font-size: 0.95rem;

  @media print {
    gap: 2pt;
    margin-top: 4pt;
    font-size: 12pt;
    line-height: 1.35;
  }
`;

export const RoleLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.4rem;

  /* print: one running line of text, "Name, Institution; Name, Institution" */
  @media print {
    display: block;
  }
`;

export const RoleLabel = styled.span`
  color: ${MUTED};
  font-size: 0.72rem;
  font-weight: bold;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-right: 0.2rem;

  @media print {
    font-size: 9pt;
    color: ${ACCENT_TEXT};
    letter-spacing: 0.1em;
  }
`;

export const GroupHeading = styled.div`
  flex-basis: 100%;
  font-weight: bold;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${ACCENT_TEXT};
  margin-top: 0.3rem;
`;

export const Label = styled.span`
  font-style: italic;
  color: ${MUTED};
  padding: 0 0.3rem;
`;

export const Institution = styled.span`
  color: ${MUTED};

  /* print: names are 12pt bold ink, their institution 10.5pt regular grey */
  @media print {
    font-size: 10.5pt;
    font-weight: normal;
  }
`;

export const Note = styled.span`
  color: ${MUTED};
  font-style: italic;
  font-size: 0.88rem;

  @media print {
    font-size: inherit;
  }
`;

// Display elements (design §4.4.1): text, quote, heading; style HIGHLIGHTED_BOX is a highlighted box.
const box = css`
  background: linear-gradient(120deg, #fbe3ea, #e3f0fa);
  border-radius: 14px;
  padding: 1rem 1.25rem;

  @media print {
    background: none;
    border: 0.75pt solid #e3c7d3;
  }
`;

export const ElementText = styled.div`
  max-width: 920px;
  margin: 0.9rem auto;
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-line;
  ${(p) => p.$box && box}
`;

export const ElementQuote = styled.blockquote`
  max-width: 920px;
  margin: 1rem auto;
  padding: 0.4rem 0 0.4rem 1.1rem;
  border-left: 3px solid #e6a5bd;
  color: #5a2a48;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.08rem;
  font-style: italic;
  line-height: 1.55;
  white-space: pre-line;
  ${(p) => p.$box && css`
    ${box}
    border-left: none;
    text-align: center;
  `}

  @media print {
    font-size: 12.5pt;
  }
`;

export const ElementHeading = styled.h4`
  max-width: 920px;
  margin: 1.4rem auto 0.7rem;
  font-size: 1.15rem;
  color: ${INK};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  ${(p) => p.$box && box}
`;

// Display elements the admin placed after a time slot: aligned with the blocks, not the time column.
export const SlotAfter = styled.div`
  max-width: 920px;
  margin: -4px auto 22px;
  padding-left: ${TIME_COL + TIME_GAP}px;

  @media screen and (max-width: 768px) {
    padding-left: 0;
  }

  @media print {
    padding-left: 78pt;
  }
`;

export const FullRow = styled.div`
  grid-column: 1 / -1;
`;

export const SlidesLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: ${(p) => (p.$small ? '0' : '0.5rem')};
  padding: ${(p) => (p.$small ? '0.1rem 0.5rem' : '0.25rem 0.75rem')};
  border: 1px solid #b9d4e8;
  background: white;
  color: ${ACCENT_DARK};
  font-size: ${(p) => (p.$small ? '0.76rem' : '0.86rem')};
  font-weight: bold;
  text-decoration: none;
  vertical-align: middle;

  &:hover, &:focus-visible {
    background: ${CHIP_HOVER};
  }

  @media print {
    display: none;
  }
`;

export const UntimedHeading = styled.h3`
  max-width: 920px;
  font-size: 1.35rem;
  color: ${INK};
  margin: 40px auto 6px;
`;

export const RoomHeading = styled.h4`
  max-width: 920px;
  font-size: 0.95rem;
  color: ${ACCENT_TEXT};
  margin: 16px auto 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const UntimedList = styled.div`
  position: relative;

  & > *:has([role="dialog"]) {
    z-index: 20;
  }

  max-width: 920px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// ------------------------------------------------------------------ person names and their card

export const NameWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 100%;
  margin-right: 0.35rem;

  /* institution and display note, right after the pill; on phones on their own line under the name */
  & > [data-after] {
    margin-left: 0.15rem;
    font-size: 0.88rem;
  }

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-right: 0;

    & > [data-after] {
      margin: 0.15rem 0 0 2.4rem;
      font-size: 0.82rem;
    }
  }
`;

// The ", " before an inline institution: dropped on phones, where the institution has its own line.
export const Sep = styled.span`
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

// A named person: a pill with the round portrait and the name. The portrait is decoration (alt=""),
// so the button's accessible name is the printed name.
export const NameButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  max-width: 100%;
  background: ${CHIP};
  border: none;
  border-radius: 999px;
  padding: 3px 12px 3px 3px;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 600;
  color: ${INK};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s, box-shadow 0.15s, transform 0.15s;

  &:hover, &:focus-visible {
    background: ${CHIP_HOVER};
    box-shadow: 0 4px 12px rgba(71, 171, 219, 0.25);
    transform: translateY(-1px);
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${ACCENT_DARK};
  }

  ${(p) => p.$highlight && css`
    background: rgba(20, 203, 222, 0.25);
    box-shadow: 0 0 0 2px ${HIGHLIGHT};
  `}

  ${reducedMotion}

  @media print {
    background: none;
    padding: 0;
    font-size: inherit;
    gap: 3pt;
  }
`;

// Print: a named person as portrait and name inline in the running text of a role line.
export const PrintName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3pt;
  vertical-align: middle;
  margin: 1pt 0;

  strong {
    color: ${INK};
  }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.98); }
`;

const sheetIn = keyframes`
  from { transform: translateY(100%); }
`;

export const Card = styled.span`
  position: absolute;
  z-index: 30;
  top: calc(100% + 10px);
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 300px;
  background: white;
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(11, 31, 58, 0.25);
  padding: 1.1rem 1.15rem 1.15rem;
  color: ${INK};
  font-size: 0.95rem;
  font-weight: normal;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: anywhere;
  animation: ${cardIn} 0.18s ease-out;
  ${reducedMotion}

  /* bridges the gap to the name, so the pointer can move into the card without closing it */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: -12px;
    height: 12px;
  }

  /* Phones: a sheet at the bottom of the screen, always inside the viewport. Wider screens keep the
     card under the name; PersonName shifts it back when it would leave the viewport. */
  @media screen and (max-width: 600px) {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    border-radius: 22px 22px 0 0;
    padding: 1.2rem 1.2rem calc(1.2rem + env(safe-area-inset-bottom));
    box-shadow: 0 -10px 40px rgba(11, 31, 58, 0.3);
    transform: none !important;
    animation: ${sheetIn} 0.25s ease-out;
    ${reducedMotion}
  }
`;

// Phones only: dims the page behind the sheet.
export const SheetBackdrop = styled.span`
  display: none;

  @media screen and (max-width: 600px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 29;
    background: rgba(6, 13, 37, 0.45);
  }
`;

export const CardTop = styled.span`
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;

export const CardText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

export const CardName = styled.span`
  font-weight: 800;
  font-size: 1.05rem;
  line-height: 1.25;
`;

export const CardActions = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

// The site's button shape: square corners, like REGISTRIEREN
const cardButton = css`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: bold;
  font-size: 0.86rem;
  padding: 0.5rem 0.95rem;
  cursor: pointer;
  text-decoration: none;
  transition: box-shadow 0.2s;

  &:hover, &:focus-visible {
    box-shadow: 0 0 0 2px ${ACCENT};
    outline: none;
  }
`;

export const CardAction = styled.button`
  ${cardButton}
  background: white;
  color: ${ACCENT_DARK};
  border: 1px solid #c4d6e6;
`;

export const ProfileLink = styled.a`
  ${cardButton}
  background: ${ACCENT_DARK};
  color: white;
  border: none;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.6rem;
  border: none;
  background: none;
  color: ${MUTED};
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.2rem 0.4rem;

  @media screen and (min-width: 601px) {
    display: none;
  }
`;

const round = (size) => css`
  width: ${size}px;
  height: ${size}px;
  min-width: ${size}px;
  border-radius: 50%;
`;

export const Photo = styled.img`
  ${(p) => round(p.$size || 64)}
  object-fit: cover;
  background: #dfe9f2;
`;

export const Initials = styled.span`
  ${(p) => round(p.$size || 64)}
  background: linear-gradient(135deg, #f6c9d6, #cfe6f5);
  color: ${INK};
  font-weight: bold;
  font-size: ${(p) => Math.round((p.$size || 64) / 3)}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// An organization's logo is fitted into its frame, never cropped round (§7.6 "Organizations").
export const LogoFrame = styled.span`
  width: ${(p) => p.$size || 110}px;
  height: ${(p) => p.$size || 110}px;
  min-width: ${(p) => p.$size || 110}px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 6px 18px rgba(11, 31, 58, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

export const LogoFallback = styled.span`
  color: ${ACCENT_TEXT};
  display: flex;
`;

// ------------------------------------------------------------------ people grid and bodies

export const SubTitle = styled.h3`
  color: ${TITLE};
  text-align: center;
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 80px 0 34px;

  @media screen and (max-width: 768px) {
    font-size: 1.45rem;
    margin: 48px 0 24px;
  }

  @media print {
    font-size: 17pt;
    color: ${INK};
    margin: 18pt 0 10pt;
    break-after: avoid;
  }
`;

export const Grid = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 2rem 1.25rem;
  max-width: 1000px;
  padding: 0;
  margin: 0 auto;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.4rem 0.75rem;
  }

  @media print {
    grid-template-columns: repeat(6, 1fr);
    gap: 6pt 4pt;
    overflow-wrap: normal;

    /* small portraits: the PDF is about the program, the faces are a compact who's who */
    img, li > * > span:first-child {
      width: 40pt !important;
      height: 40pt !important;
      min-width: 40pt !important;
      font-size: 12pt !important;
    }
  }
`;

export const GridItem = styled.li`
  min-width: 0;

  @media print {
    break-inside: avoid;
  }
`;

const tileBase = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  background: none;
  border: none;
  border-radius: 18px;
  color: ${INK};
  font: inherit;
  padding: 0.5rem 0.3rem;

  & > img, & > span:first-child {
    transition: transform 0.25s, box-shadow 0.25s;
  }

  ${reducedMotion}
`;

export const PersonTile = styled.button`
  ${tileBase}
  cursor: pointer;

  &:hover > img, &:hover > span:first-child, &:focus-visible > img, &:focus-visible > span:first-child {
    transform: scale(1.05);
    box-shadow: 0 10px 26px rgba(11, 31, 58, 0.18);
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT_DARK};
    outline-offset: 2px;
  }

  ${(p) => p.$active && css`
    background: rgba(255, 255, 255, 0.7);
    box-shadow: 0 0 0 3px ${HIGHLIGHT};
  `}
`;

export const StaticTile = styled.div`
  ${tileBase}
`;

export const TileName = styled.span`
  font-weight: bold;
  font-size: 0.98rem;
  line-height: 1.3;
  margin-top: 0.8rem;

  @media print {
    font-size: 10.5pt;
    margin-top: 3pt;
  }
`;

export const TileInstitution = styled.span`
  color: ${MUTED};
  font-size: 0.85rem;
  margin-top: 0.2rem;

  @media print {
    font-size: 9.5pt;
  }
`;

export const TileRoles = styled.span`
  color: ${ACCENT_TEXT};
  font-size: 0.74rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 0.3rem;

  @media print {
    font-size: 8.5pt;
  }
`;

export const OrgLink = styled.a`
  ${tileBase}
  text-decoration: none;

  &:hover > span:first-child, &:focus-visible > span:first-child {
    transform: scale(1.04);
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT_DARK};
    outline-offset: 2px;
  }
`;

export const BodyTitle = styled.h4`
  color: ${INK};
  text-align: center;
  font-size: 1.35rem;
  font-weight: 800;
  margin: 52px 0 24px;

  @media screen and (max-width: 768px) {
    font-size: 1.15rem;
    margin: 32px 0 16px;
  }

  @media print {
    font-size: 14pt;
    margin: 12pt 0 8pt;
  }
`;

// The sponsor pages (ProgramPages.SponsorPages): designed pages, so no card around them, at the
// timeline's width. Printed, each one fills a page of its own (a little less than PRINT_PAGE_MM, so
// rounding never pushes it over the page), the image fitted in, above the flowers.
export const SponsorBlock = styled.section`
  position: relative;
  z-index: 1;
  max-width: 920px;
  margin: 8px auto 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media print {
    max-width: none;
    margin: 0;
    gap: 0;
    ${(p) => p.$breakAfter && css`break-after: page;`}
  }
`;

export const SponsorPage = styled.div`
  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 10px 24px rgba(11, 31, 58, 0.14);
  }

  @media print {
    break-before: page;
    break-inside: avoid;
    height: ${PRINT_PAGE_MM - 3}mm;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: auto;
      max-width: 100%;
      max-height: 100%;
      border-radius: 0;
      box-shadow: none;
    }
  }
`;

// A title page of the admin's own print (ProgramPages.CoverPrintPages): a page of its own in front of
// the program, the image fitted in.
export const CoverPrintPage = styled.div`
  @media print {
    break-after: page;
    break-inside: avoid;
    height: ${PRINT_PAGE_MM - 3}mm;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      display: block;
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

const turn = keyframes`
  to { transform: rotate(360deg); }
`;

// In place of a title or sponsor page image that is still loading (ProgramPages): a calm box with a
// turning icon, so the empty place does not look broken.
export const PageLoading = styled.div`
  grid-area: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 260px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.6);
  color: #64748b;
  font-size: 0.9rem;

  svg {
    animation: ${turn} 1s linear infinite;
    ${reducedMotion}
  }
`;

// The flowers of the pages after the sponsor pages (ProgramPages.SponsorPages): a layer of no height at
// the top of the page after them, behind the content that follows (SectionInner's stacking context).
export const PrintDecoAfter = styled.div`
  display: none;

  @media print {
    display: block;
    position: relative;
    z-index: -1;
    height: 0;
    pointer-events: none;
  }
`;

// The title pages in place of the event page's main image (ProgramPages.CoverPages): stacked in one
// cell, the shown one faded in; with more than one, an arrow at each edge while the pointer is over
// them or a key is in them (always, faintly, on touch screens).
export const CoverFrame = styled.div`
  position: relative;
  display: grid;
  max-width: 100%;
  margin-bottom: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
`;

export const CoverImage = styled.img`
  grid-area: 1 / 1;
  display: block;
  max-width: 100%;
  height: auto;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity 0.9s ease;
  ${reducedMotion}
`;

export const CoverArrow = styled.button`
  position: absolute;
  top: 50%;
  ${(p) => p.$side}: 10px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  color: ${INK};
  box-shadow: 0 2px 8px rgba(11, 31, 58, 0.25);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease;
  ${reducedMotion}

  ${CoverFrame}:hover &,
  ${CoverFrame}:focus-within & {
    opacity: 1;
  }

  &:hover { background: #fff; }
  &:focus-visible { opacity: 1; outline: 2px solid ${ACCENT_DARK}; outline-offset: 2px; }

  @media (hover: none) {
    opacity: 0.7;
  }
`;

// A body member's tile that leads to their public profile (the tile of OrgLink, for a person).
export const ProfileTile = styled(OrgLink)``;

// The small "Profile" link under a person tile of the people grid, whose click highlights the
// person's entries in the timeline.
export const TileProfileLink = styled.a`
  display: block;
  text-align: center;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${ACCENT_DARK};
  text-decoration: none;

  &:hover, &:focus-visible {
    text-decoration: underline;
  }
`;
